import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import useAuthenticatedFetch from "../hooks/useAuthenticatedFetch";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from 'react-i18next';
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CommunitySelectDialog from "./dialogs/CommunitySelectDialog";
import { isValidEmail } from "../utils/validation";

export function LoginForm() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [communities, setCommunities] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const { login, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");

  const fetchWithAuth = useAuthenticatedFetch();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleSignIn = (e) => {
    e.preventDefault();
    if (isLoading) return;

    setIsLoading(true);
    setError("");
    fetch(`${process.env.REACT_APP_BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json();
        } else {
          const json = await response.json().catch(() => null);
          throw new Error(json?.message || "Échec de la connexion");
        }
      })
      .then(async (data) => {
        const storedUser = await login(data);
        return fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/community/getUserCommunities/${storedUser.userId}`,
          {},
          storedUser
        )
          .then((communitiesResponse) => {
            if (communitiesResponse.ok) {
              return communitiesResponse.json();
            } else {
              throw new Error("Erreur lors de la récupération des communautés");
            }
          })
          .then((communitiesData) => {
            if (communitiesData.length > 1) {
              setCommunities(communitiesData);
              setShowDialog(true);
            } else {
              login({ ...storedUser, communityId: communitiesData[0]?.id });
              navigate("/AdminDashboard");
            }
          });
      })
      .catch((error) => {
        console.error("Erreur :", error);
        setError(error?.message || String(error));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCommunitySelect = (community) => {
    login({ ...user, communityId: community.id });
    setShowDialog(false);
    navigate("/AdminDashboard");
  };

  const openForgotDialog = () => {
    setForgotEmail(email);
    setForgotError("");
    setForgotSuccess("");
    setForgotOpen(true);
  };

  const closeForgotDialog = () => {
    setForgotOpen(false);
    setForgotError("");
    setForgotSuccess("");
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    setForgotError("");
    setForgotSuccess("");

    if (!forgotEmail) {
      setForgotError(t("forgotPassword.errors.emailRequired"));
      return;
    }
    if (!isValidEmail(forgotEmail)) {
      setForgotError(t("forgotPassword.errors.emailInvalid"));
      return;
    }

    setForgotLoading(true);

    fetch(`${process.env.REACT_APP_BASE_URL}/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: forgotEmail, language: i18n.language }),
    })
      .then(async (response) => {
        const json = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(json?.message || t("forgotPassword.errors.submit"));
        }
        setForgotSuccess(t("forgotPassword.success"));
      })
      .catch((err) => {
        setForgotError(err?.message || t("forgotPassword.errors.submit"));
      })
      .finally(() => {
        setForgotLoading(false);
      });
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSignIn}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "90%",
          maxWidth: 400,
          margin: "auto",
          padding: theme.spacing(4),
          position: "absolute",
          top: "40%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <Typography
          component="h2"
          variant="h4"
          sx={{
            textAlign: "center",
            color: theme.palette.text.primary,
            fontWeight: theme.typography.fontWeightBold,
          }}
        >
          {t('login.welcome')}
        </Typography>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <TextField
          id="email"
          label={t('login.emailAddress')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
          InputProps={{
            sx: {
              textAlign: i18n.language === 'he' ? 'right' : 'left',
            },
          }}
          InputLabelProps={{
            sx: {
              right: i18n.language === 'he' ? 14 : 'unset',
              left: i18n.language === 'he' ? 'unset' : 14,
              transformOrigin: i18n.language === 'he' ? 'right' : 'left',
              backgroundColor: theme.palette.background.paper,
              padding: '0 8px',
            },
          }}
        />

        <TextField
          id="password"
          label={t('login.password')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          required
          InputProps={{
            sx: {
              textAlign: i18n.language === 'he' ? 'right' : 'left',
            },
          }}
          InputLabelProps={{
            sx: {
              right: i18n.language === 'he' ? 14 : 'unset',
              left: i18n.language === 'he' ? 'unset' : 14,
              transformOrigin: i18n.language === 'he' ? 'right' : 'left',
              backgroundColor: theme.palette.background.paper,
              padding: '0 8px',
            },
          }}
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={isLoading}
          sx={{
            padding: theme.spacing(1.5),
            fontSize: theme.typography.body1.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          {isLoading ? t('login.loggingIn') : t('login.logIn')}
        </Button>

        <Typography
          component="button"
          type="button"
          variant="body2"
          onClick={openForgotDialog}
          sx={{
            textAlign: "center",
            color: theme.palette.text.secondary,
            textDecoration: "underline",
            cursor: "pointer",
            border: "none",
            background: "none",
            padding: 0,
            font: "inherit",
          }}
        >
          {t("login.forgotPassword")}
        </Typography>
      </Box>

      <Dialog open={forgotOpen} onClose={closeForgotDialog} fullWidth maxWidth="xs">
        <Box component="form" onSubmit={handleForgotPassword}>
          <DialogTitle>{t("forgotPassword.title")}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("forgotPassword.description")}
            </Typography>
            {forgotError ? <Alert severity="error" sx={{ mb: 2 }}>{forgotError}</Alert> : null}
            {forgotSuccess ? <Alert severity="success" sx={{ mb: 2 }}>{forgotSuccess}</Alert> : null}
            <TextField
              autoFocus
              label={t("forgotPassword.emailLabel")}
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              fullWidth
              required
              disabled={forgotLoading || Boolean(forgotSuccess)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeForgotDialog} disabled={forgotLoading}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={forgotLoading || Boolean(forgotSuccess)}
            >
              {forgotLoading ? t("forgotPassword.sending") : t("forgotPassword.submit")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <CommunitySelectDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        communities={communities}
        onCommunitySelect={handleCommunitySelect}
      />
    </>
  );
}

export default LoginForm;